// ── Design decisions ──
// Pestaña de captura del viaje: arriba la franja con referencia + estado +
// indicador de guardado, luego el riel horizontal de bultos, el panel para
// analizar una foto de la lista y, ocupando el alto restante, el ledger de
// paquetes/artículos (ProduccionPanel compartido con el Anexo).
//
// · No se duplica el editor: ProduccionPanel ya sabe de paquetes, filas,
//   autofoco y totales. Lo propio del viajero entra por los dos render-props:
//   renderPaqueteExtras (cliente / bulto / tipo de ingreso / quitar paquete)
//   y renderItemExtras (Nuevo-Usado, catálogo DGA, procedencia, revisión).
// · Al elegir cliente también se actualiza `nombre` del paquete, así el
//   ledger y el manifiesto muestran lo mismo sin que el operador tipee dos veces.
// · "Analizar lista": TRES rutas — foto, texto pegado y PDF — y cada una cae
//   al mismo drawer con la misma forma de resultado; el drawer no sabe cuál
//   corrió. XLSX / DOCX siguen como chips deshabilitados "Próximamente".
//   - Foto: como siempre, pero la imagen se reduce en el cliente a 1 568 px de
//     lado largo (JPEG ~q85) antes del base64 — silencioso, y si el canvas no
//     está (jsdom, formatos raros) se manda la original tal cual.
//   - Pegar texto: parser determinista local (shared/parseListaTexto) — cero
//     red, cero tokens, instantáneo: nada de spinner ni esCached; la fuente
//     viaja como source_meta { source_tipo: 'texto', texto } y el chip
//     "Análisis local — sin costo" lo pinta SourcePane.
//   - PDF: mismo Edge Function encomienda-list-extract vía el adapter sellado.
//     El CONTRATO acepta el par legado image_base64 + image_media_type y rutea
//     por media type, así que un PDF viaja como image_base64 con
//     image_media_type 'application/pdf' sin tocar el adapter. Guardia de
//     tamaño en el cliente (>10 MB ⇒ error en español, sin llamar a la API),
//     espejo de la 413 del servidor. Sin vista previa: SourcePane muestra la
//     tarjeta del archivo.
//   La vista previa de foto usa un object URL (con guarda para jsdom) que se
//   revoca al cerrar el drawer.
// · readOnly (viaje cerrado): banner slate + fieldset disabled que apaga todos
//   los controles de una vez; ProduccionPanel no tiene prop disabled y así no
//   hay que tocarlo. Las extras reciben además disabled explícito para que el
//   estilo apagado sea consistente.
// · Quitar paquete es de dos toques (armar → confirmar, desarme a los 4 s),
//   mismo patrón que AnexoItemRow y BultoCard.
// · Borrador de extracción (viajero_extraction_drafts, uno por viaje): si el
//   operador cierra el drawer sin Confirmar/Descartar, el estado se guarda vía
//   api.guardarBorradorExtraccion y la pista amarilla se reemplaza por el
//   banner ámbar "Tenés una extracción sin confirmar · Ver última entrada",
//   que reabre el drawer hidratado desde el borrador (cero tokens). Confirmar y
//   Descartar borran el borrador. La foto se guarda en source_meta solo si su
//   base64 cabe en LIMITE_IMAGEN_BORRADOR; si no, el borrador se retoma sin
//   vista previa. Las tres funciones de borrador son opcionales en `api`
//   (los stubs viejos no las traen) y su falla nunca bloquea la extracción.
// · tenant_id viaja en el cuerpo de la extracción: es lo que enciende la caché
//   por hash de contenido de la Edge Function; `cached: true` se muestra como
//   chip neutro en el drawer.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Camera,
  ClipboardPaste,
  FileSpreadsheet,
  FileText,
  FileUp,
  History,
  Loader2,
  Lock,
  Trash2,
} from 'lucide-react';
import { parseListaTexto } from './shared/parseListaTexto';
import ProduccionPanel from '../anexo/ProduccionPanel';
import BultoRail, { BultoSelect } from './shared/BultoRail';
import ClientePicker from './shared/ClientePicker';
import TipoIngresoControl from './shared/TipoIngresoControl';
import CatalogHoverPicker from './shared/CatalogHoverPicker';
import IsNewToggle from './shared/IsNewToggle';
import { EstadoChip, ProvenanceChip, ReviewDot } from './shared/chips';
import ExtractionDrawer from './ExtractionDrawer';

const ESTADO_COPY = {
  inactivo: '',
  guardando: 'Guardando…',
  guardado: 'Guardado',
  fallo: 'No se pudo guardar',
};

// ~1.9 MB de imagen: por encima de esto el borrador se guarda sin la foto.
export const LIMITE_IMAGEN_BORRADOR = 2_500_000;

// Guardia de PDF en el cliente: espejo de la 413 de la Edge Function (~10 MB
// decodificados), con el MISMO mensaje en español, para no gastar la subida.
export const LIMITE_PDF_BYTES = 10 * 1024 * 1024;
export const ERROR_PDF_GRANDE = 'El PDF es demasiado grande — subí una foto o un PDF más liviano.';

// Lado largo máximo de una foto antes de subirla: reduce tokens de visión sin
// perder legibilidad de manuscrita (resolución nativa del modelo).
export const LADO_MAX_FOTO = 1568;

const leerComoBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || '');
      const coma = res.indexOf(',');
      resolve(coma >= 0 ? res.slice(coma + 1) : res);
    };
    reader.onerror = () => reject(new Error('No se pudo leer la imagen.'));
    reader.readAsDataURL(file);
  });

// Reduce la foto a LADO_MAX_FOTO de lado largo (JPEG ~q85) y devuelve
// { base64, mediaType }, o null si no hace falta o no se puede (jsdom sin
// canvas, formato raro): el que llama cae a la original — silencioso, nunca
// bloquea la extracción. Exportada para poder probarla con canvas simulado.
export const reducirImagen = async (file) => {
  try {
    if (typeof createImageBitmap !== 'function') return null;
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const lado = Math.max(width, height);
    if (!lado || lado <= LADO_MAX_FOTO) {
      bitmap.close?.();
      return null;
    }
    const escala = LADO_MAX_FOTO / lado;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * escala);
    canvas.height = Math.round(height * escala);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const coma = dataUrl.indexOf(',');
    if (coma < 0 || !dataUrl.startsWith('data:image/jpeg')) return null;
    return { base64: dataUrl.slice(coma + 1), mediaType: 'image/jpeg' };
  } catch (_) {
    return null;
  }
};

// Botón "Quitar paquete" de dos toques.
const QuitarPaqueteButton = ({ onQuitar, disabled }) => {
  const [confirmar, setConfirmar] = useState(false);
  useEffect(() => {
    if (!confirmar) return undefined;
    const t = setTimeout(() => setConfirmar(false), 4000);
    return () => clearTimeout(t);
  }, [confirmar]);
  if (confirmar) {
    return (
      <button
        type="button"
        onClick={onQuitar}
        disabled={disabled}
        aria-label="Confirmar eliminación del paquete"
        className="rounded-md border border-red-300 bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-600 disabled:opacity-50"
      >
        Confirmar
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setConfirmar(true)}
      disabled={disabled}
      aria-label="Quitar paquete"
      title="Quitar paquete"
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
    >
      <Trash2 size={12} aria-hidden="true" /> Quitar paquete
    </button>
  );
};

const NuevaEntradaTab = ({
  viaje,
  customers = [],
  estadoGuardado = 'inactivo',
  ultimoAgregadoId,
  ultimoPaqueteId,
  readOnly = false,
  acciones,
  api,
}) => {
  const [extraccion, setExtraccion] = useState('idle'); // idle | cargando | error | listo
  const [errorExtraccion, setErrorExtraccion] = useState('');
  const [resultado, setResultado] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [cached, setCached] = useState(false);
  const [sourceMeta, setSourceMeta] = useState(null);
  // Borrador guardado en la API para este viaje (banner de recuperación) y el
  // payload con el que se hidrata el drawer cuando se retoma.
  const [borrador, setBorrador] = useState(null);
  const [borradorHidratado, setBorradorHidratado] = useState(null);
  // Ruta "Pegar texto": área visible + contenido pegado (persiste tras
  // analizar, para reintentar o corregir sin volver a pegar).
  const [modoTexto, setModoTexto] = useState(false);
  const [textoLista, setTextoLista] = useState('');
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const ultimoArchivo = useRef(null);
  const sourceMetaRef = useRef(null);
  sourceMetaRef.current = sourceMeta;
  // `api` y `acciones` son objetos nuevos en cada render del contenedor: se
  // leen por ref para que los callbacks que bajan al drawer sean estables.
  const apiRef = useRef(api);
  apiRef.current = api;
  const accionesRef = useRef(acciones);
  accionesRef.current = acciones;

  const bultos = viaje?.bultos || [];
  const paquetes = viaje?.paquetes || [];
  const viajeId = viaje?.id ?? null;

  // Al abrir el viaje: ¿quedó una extracción sin confirmar? Se depende de la
  // función (estable, viene del módulo) y no del objeto `api`, que el
  // contenedor arma en cada render.
  const obtenerBorrador = api?.obtenerBorradorExtraccion;
  useEffect(() => {
    let cancelado = false;
    setBorrador(null);
    if (!viajeId || typeof obtenerBorrador !== 'function') return undefined;
    obtenerBorrador(viajeId)
      .then((b) => {
        if (!cancelado) setBorrador(b && b.payload ? b : null);
      })
      .catch((err) => console.error('[NuevaEntradaTab] no se pudo leer el borrador de extracción', err));
    return () => {
      cancelado = true;
    };
  }, [viajeId, obtenerBorrador]);

  const revocarUrl = useCallback((url) => {
    if (url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        /* noop */
      }
    }
  }, []);

  useEffect(() => () => revocarUrl(imageUrl), [imageUrl, revocarUrl]);

  const analizar = useCallback(
    async (file) => {
      if (!file || readOnly) return;
      const esPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
      ultimoArchivo.current = file;
      // Guardia de tamaño ANTES de leer o subir nada: mismo texto que la 413.
      if (esPdf && file.size > LIMITE_PDF_BYTES) {
        setErrorExtraccion(ERROR_PDF_GRANDE);
        setExtraccion('error');
        return;
      }
      setExtraccion('cargando');
      setErrorExtraccion('');
      try {
        let image_base64;
        let image_media_type;
        if (esPdf) {
          image_base64 = await leerComoBase64(file);
          // El CONTRATO rutea por media type: el par legado image_* con
          // 'application/pdf' manda un bloque document, no image.
          image_media_type = 'application/pdf';
        } else {
          // Foto: reducción silenciosa a LADO_MAX_FOTO; si no se puede, la
          // original viaja tal cual (comportamiento de siempre).
          const reducida = await reducirImagen(file);
          image_base64 = reducida ? reducida.base64 : await leerComoBase64(file);
          image_media_type = reducida ? reducida.mediaType : file.type || 'image/jpeg';
        }
        const res = await api.extractLista({
          image_base64,
          image_media_type,
          // Enciende la caché por hash de contenido de la Edge Function.
          tenant_id: viaje?.tenant_id ?? null,
        });
        const url =
          !esPdf && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
            ? URL.createObjectURL(file)
            : null;
        setImageUrl((prev) => {
          revocarUrl(prev);
          return url;
        });
        setSourceMeta(
          esPdf
            ? {
                source_tipo: 'pdf',
                file_name: file.name || 'documento.pdf',
                image_media_type,
                content_hash: res?.content_hash ?? null,
                image_base64: null,
              }
            : {
                source_tipo: 'imagen',
                image_media_type,
                content_hash: res?.content_hash ?? null,
                image_base64: image_base64.length <= LIMITE_IMAGEN_BORRADOR ? image_base64 : null,
              }
        );
        setCached(Boolean(res?.cached));
        setBorradorHidratado(null);
        setResultado(res);
        setExtraccion('listo');
        setDrawerAbierto(true);
      } catch (err) {
        console.error('[NuevaEntradaTab] falló la extracción', err);
        setErrorExtraccion(err?.message || 'No se pudo analizar la lista. Intente de nuevo.');
        setExtraccion('error');
      }
    },
    [api, readOnly, revocarUrl, viaje?.tenant_id]
  );

  const onArchivo = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) analizar(file);
    // Permitir volver a elegir el mismo archivo.
    e.target.value = '';
  };

  // Ruta de texto: parser local, síncrono y gratis — nada de estados de carga.
  const analizarTexto = useCallback(() => {
    const texto = textoLista;
    if (!texto.trim() || readOnly) return;
    const res = parseListaTexto(texto);
    setImageUrl((prev) => {
      revocarUrl(prev);
      return null;
    });
    setSourceMeta({ source_tipo: 'texto', texto });
    setCached(false);
    setBorradorHidratado(null);
    setResultado(res);
    setErrorExtraccion('');
    setExtraccion('listo');
    setDrawerAbierto(true);
  }, [textoLista, readOnly, revocarUrl]);

  const cerrarDrawer = useCallback(() => {
    setDrawerAbierto(false);
    setExtraccion('idle');
    setResultado(null);
    setBorradorHidratado(null);
    setCached(false);
    setSourceMeta(null);
    setImageUrl((prev) => {
      revocarUrl(prev);
      return null;
    });
  }, [revocarUrl]);

  // Esc / ✕ / fondo del drawer: se guarda lo editado para retomarlo después.
  const guardarBorrador = useCallback(
    (payload) => {
      if (!viajeId || !payload) return;
      const meta = sourceMetaRef.current;
      // Optimista: el banner aparece aunque la red tarde.
      setBorrador({ encomienda_id: viajeId, payload, source_meta: meta, created_at: new Date().toISOString() });
      const guardar = apiRef.current?.guardarBorradorExtraccion;
      if (typeof guardar !== 'function') return;
      guardar(viajeId, payload, meta)
        .then((b) => {
          if (b && b.payload) setBorrador(b);
        })
        .catch((err) => console.error('[NuevaEntradaTab] no se pudo guardar el borrador de extracción', err));
    },
    [viajeId]
  );

  const eliminarBorrador = useCallback(() => {
    setBorrador(null);
    const eliminar = apiRef.current?.eliminarBorradorExtraccion;
    if (!viajeId || typeof eliminar !== 'function') return;
    eliminar(viajeId).catch((err) =>
      console.error('[NuevaEntradaTab] no se pudo eliminar el borrador de extracción', err)
    );
  }, [viajeId]);

  // Confirmar: persiste en el viaje y, solo si eso salió bien, borra el borrador.
  const confirmarExtraccion = useCallback(
    async (payload) => {
      await accionesRef.current.confirmarExtraccion(payload);
      eliminarBorrador();
    },
    [eliminarBorrador]
  );

  // "Ver última entrada": reabre el drawer desde el borrador, sin analizar nada.
  const verUltimaEntrada = useCallback(() => {
    if (!borrador?.payload) return;
    const meta = borrador.source_meta || null;
    const dataUrl =
      meta?.image_base64 && meta?.image_media_type
        ? `data:${meta.image_media_type};base64,${meta.image_base64}`
        : null;
    setImageUrl((prev) => {
      revocarUrl(prev);
      return dataUrl;
    });
    setSourceMeta(meta);
    setCached(false);
    setResultado(null);
    setBorradorHidratado(borrador.payload);
    setExtraccion('listo');
    setDrawerAbierto(true);
  }, [borrador, revocarUrl]);

  const renderPaqueteExtras = (paquete) => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-100 bg-[#f8fafc] px-3 py-2">
      <ClientePicker
        id={`cliente-${paquete.id}`}
        value={paquete.customer_id}
        customers={customers}
        onChange={(customer_id, c) =>
          acciones.cambiarPaquete(paquete.id, {
            customer_id,
            nombre: c?.full_name ?? paquete.nombre,
          })
        }
        onQuickCreate={acciones.quickCreateCustomer}
        disabled={readOnly}
      />
      <BultoSelect
        bultos={bultos}
        value={paquete.bulto_id}
        onChange={(bulto_id) => acciones.cambiarPaquete(paquete.id, { bulto_id })}
        disabled={readOnly}
      />
      <TipoIngresoControl
        compact
        value={paquete.tipo_ingreso}
        onChange={(tipo_ingreso) => acciones.cambiarPaquete(paquete.id, { tipo_ingreso })}
        disabled={readOnly}
      />
      <span className="ml-auto">
        <QuitarPaqueteButton onQuitar={() => acciones.quitarPaquete(paquete.id)} disabled={readOnly} />
      </span>
    </div>
  );

  const renderItemExtras = (item) => (
    <div className="mb-1 flex flex-wrap items-center gap-2 px-2 pb-1.5 text-[11px]">
      <IsNewToggle
        value={item.is_new}
        onChange={(is_new) => acciones.cambiarItem(item.id, { is_new })}
        disabled={readOnly}
      />
      <CatalogHoverPicker
        item={item}
        searchCatalog={api.searchCatalog}
        disabled={readOnly}
        onLock={(row) =>
          // Ligar NO toca `descripcion` (texto crudo intacto): la fila ligada
          // muestra catalogo_descripcion y Desligar restaura el crudo solo.
          acciones.cambiarItem(
            item.id,
            row
              ? {
                  customs_catalog_id: row.id,
                  catalogo_descripcion: row.descripcion ?? null,
                  catalogo_unidad_medida: row.unidad_medida ?? null,
                  cip_unitario_usd: Number(row.cip_usd) || null,
                  unidad_medida: row.unidad_medida || item.unidad_medida,
                  fob_unitario_usd: Number(row.fob_usd) || item.fob_unitario_usd,
                  manual_override: false,
                }
              : {
                  customs_catalog_id: null,
                  catalogo_descripcion: null,
                  catalogo_unidad_medida: null,
                  cip_unitario_usd: null,
                  manual_override: true,
                }
          )
        }
      />
      <ProvenanceChip source={item.extraction_source} />
      {item.requires_review && (
        <>
          <ReviewDot visible />
          <button
            type="button"
            disabled={readOnly}
            onClick={() => acciones.cambiarItem(item.id, { requires_review: false })}
            className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
          >
            Marcar revisado
          </button>
        </>
      )}
    </div>
  );

  return (
    // La pestaña es el contenedor de scroll: el panel de artículos va a altura
    // completa (alturaAuto) y su barra de totales queda sticky al pie de acá.
    // Sin padding vertical en el scroll (va en la franja y en el fieldset): así
    // los sticky quedan al ras del borde y no asoman filas por encima/debajo.
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto bg-[#f8fafc] px-3 md:px-4">
      {/* Franja superior */}
      <div className="flex flex-wrap items-center gap-3 pt-3 md:pt-4">
        <label className="flex min-w-0 flex-1 items-center gap-2 text-xs text-slate-500">
          Referencia
          <input
            type="text"
            value={viaje?.trip_reference || ''}
            onChange={(e) => acciones.setTripReference(e.target.value)}
            placeholder="Ej. Viaje Oct 15"
            aria-label="Referencia del viaje"
            disabled={readOnly}
            className="min-w-0 max-w-xs flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm font-semibold text-slate-900 outline-none focus:border-[var(--brand-teal)] disabled:opacity-60"
          />
        </label>
        <EstadoChip status={viaje?.status} />
        <span
          role="status"
          aria-live="polite"
          className={`text-[11px] ${estadoGuardado === 'fallo' ? 'text-red-600' : 'text-slate-400'}`}
        >
          {ESTADO_COPY[estadoGuardado] || ''}
        </span>
      </div>

      {readOnly && (
        <p className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700">
          <Lock size={13} aria-hidden="true" />
          Este manifiesto está cerrado. Reabrilo desde Producción para editar.
        </p>
      )}

      <fieldset disabled={readOnly} className="flex flex-1 flex-col gap-3 border-0 px-0 pb-3 pt-0 md:pb-4">
        <BultoRail
          bultos={bultos}
          paquetes={paquetes}
          onAgregar={acciones.agregarBulto}
          onChange={acciones.cambiarBulto}
          onQuitar={acciones.quitarBulto}
          disabled={readOnly}
        />

        {/* Analizar lista */}
        <section
          aria-label="Analizar lista"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
        >
          <span className="text-xs font-semibold text-slate-700">Analizar lista (foto, texto o archivo)</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            aria-label="Subir foto de la lista"
            onChange={onArchivo}
            disabled={readOnly || extraccion === 'cargando'}
            className="hidden"
          />
          <input
            ref={pdfRef}
            type="file"
            accept="application/pdf,.pdf"
            aria-label="Subir PDF de la lista"
            onChange={onArchivo}
            disabled={readOnly || extraccion === 'cargando'}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={readOnly || extraccion === 'cargando'}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {extraccion === 'cargando' ? (
              <Loader2 size={15} className="animate-spin" aria-hidden="true" />
            ) : (
              <Camera size={15} aria-hidden="true" />
            )}
            Subir foto
          </button>
          <button
            type="button"
            onClick={() => setModoTexto((m) => !m)}
            aria-expanded={modoTexto}
            disabled={readOnly || extraccion === 'cargando'}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <ClipboardPaste size={15} aria-hidden="true" />
            Pegar texto
          </button>
          <button
            type="button"
            onClick={() => pdfRef.current?.click()}
            disabled={readOnly || extraccion === 'cargando'}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <FileUp size={15} aria-hidden="true" />
            PDF
          </button>
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Próximamente"
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-400"
          >
            <FileSpreadsheet size={12} aria-hidden="true" /> XLSX
          </button>
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Próximamente"
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-400"
          >
            <FileText size={12} aria-hidden="true" /> DOCX
          </button>
          {extraccion === 'cargando' && (
            <span role="status" className="text-xs text-slate-500">
              Analizando la lista…
            </span>
          )}
          {extraccion === 'error' && (
            <span role="alert" className="inline-flex items-center gap-2 text-xs text-red-600">
              {errorExtraccion}
              <button
                type="button"
                onClick={() => analizar(ultimoArchivo.current)}
                className="font-semibold underline"
              >
                Reintentar
              </button>
            </span>
          )}

          {/* Área de pegado: ancho completo bajo la barra; análisis local
              síncrono — sin spinner, sin costo (el chip lo dice). Ctrl+Enter
              analiza en escritorio; en el teléfono el botón queda al alcance
              del pulgar bajo el textarea. */}
          {modoTexto && (
            <div className="flex basis-full flex-col gap-2">
              <textarea
                value={textoLista}
                onChange={(e) => setTextoLista(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    analizarTexto();
                  }
                }}
                rows={7}
                placeholder={'Pegá acá la lista de WhatsApp…\nEj.:\nMALETA 1\n36H. LILIAN GUERRA\n2 TALADROS NUEVOS\n4 KGS ROPA USADA'}
                aria-label="Texto de la lista"
                disabled={readOnly}
                className="min-h-[160px] w-full rounded-lg border border-slate-300 bg-white p-3 font-mono text-[13px] text-slate-900 outline-none focus:border-[var(--brand-teal)] disabled:opacity-60"
              />
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={analizarTexto}
                  disabled={readOnly || !textoLista.trim()}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                >
                  Analizar
                </button>
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                  Análisis local — sin costo
                </span>
                <span className="hidden text-[11px] text-slate-400 md:inline">Ctrl+Enter para analizar</span>
              </div>
            </div>
          )}
        </section>

        {borrador && !readOnly ? (
          <div
            role="region"
            aria-label="Extracción sin confirmar"
            className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            <History size={14} className="shrink-0" aria-hidden="true" />
            <span className="font-semibold">Tenés una extracción sin confirmar.</span>
            <button
              type="button"
              onClick={verUltimaEntrada}
              className="inline-flex min-h-[36px] items-center rounded-md border border-amber-400 bg-white px-2.5 font-semibold text-amber-900 hover:bg-amber-100"
            >
              Ver última entrada
            </button>
          </div>
        ) : (
          paquetes.length === 0 &&
          !readOnly && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Agregá el primer paquete o analizá una foto de tu lista.
            </p>
          )
        )}

        <div className="flex-1">
          <ProduccionPanel
            paquetes={paquetes}
            estadoGuardado={estadoGuardado}
            ultimoAgregadoId={ultimoAgregadoId}
            ultimoPaqueteId={ultimoPaqueteId}
            onChangeNombrePaquete={acciones.cambiarNombrePaquete}
            onChangeItem={acciones.cambiarItem}
            onQuitarItem={acciones.quitarItem}
            onAgregarItem={acciones.agregarItem}
            onAgregarPaquete={acciones.agregarPaquete}
            renderPaqueteExtras={renderPaqueteExtras}
            renderItemExtras={renderItemExtras}
            alturaAuto
          />
        </div>
      </fieldset>

      <ExtractionDrawer
        open={drawerAbierto}
        onClose={cerrarDrawer}
        resultado={resultado}
        borrador={borradorHidratado}
        imageUrl={imageUrl}
        sourceTipo={sourceMeta?.source_tipo || 'imagen'}
        sourceMeta={sourceMeta}
        cached={cached}
        customers={customers}
        bultos={bultos}
        onConfirmar={confirmarExtraccion}
        onDescartar={eliminarBorrador}
        onGuardarBorrador={guardarBorrador}
        onQuickCreate={acciones.quickCreateCustomer}
        cargando={false}
        error={null}
      />
    </div>
  );
};

export default NuevaEntradaTab;
